import { useCallback, useEffect, useState } from 'react';

export const ACTS = [
  { id: 'overview', label: 'Overview' },
  { id: 'details', label: 'Details' },
  { id: 'method', label: 'Method' },
  { id: 'plan', label: 'Plan' },
  { id: 'results', label: 'Results' },
  { id: 'next', label: 'Next Steps' },
  { id: 'close', label: 'Close' },
  { id: 'disclosures', label: 'Disclosures' },
] as const;

export const PLAN_ACT_INDEX = 3;
export const DETAILS_ACT_INDEX = 1;
export const DISCLOSURES_ACT_INDEX = 7;

export type DetailsMode = 'view' | 'edit' | 'submitted';

/**
 * Deck navigation state: act index, optimization sub-index within the Plan
 * act (null = overview grid), and the Details act's edit mode (lifted so the
 * footer's "Refine your plan" CTA can trigger it from anywhere).
 *
 * `advance()` reproduces the mockup semantics: while inside an optimization
 * deep-dive, Next/ArrowRight steps through the optimizations before moving
 * on to the Results act; Back steps back to the grid.
 */
export function useDeckState(optimizationCount: number) {
  const [act, setAct] = useState(0);
  const [opt, setOpt] = useState<number | null>(null);
  const [detailsMode, setDetailsMode] = useState<DetailsMode>('view');

  const go = useCallback((n: number) => {
    setAct(Math.max(0, Math.min(ACTS.length - 1, n)));
    setOpt(null);
  }, []);

  const goToDetailsEdit = useCallback(() => {
    setDetailsMode('edit');
    go(DETAILS_ACT_INDEX);
  }, [go]);

  const advance = useCallback(
    (dir: 1 | -1) => {
      if (act === PLAN_ACT_INDEX && opt !== null) {
        const next = opt + dir;
        if (next < 0) {
          setOpt(null);
          return;
        }
        if (next > optimizationCount - 1) {
          setAct(PLAN_ACT_INDEX + 1);
          setOpt(null);
          return;
        }
        setOpt(next);
        return;
      }
      go(act + dir);
    },
    [act, opt, optimizationCount, go]
  );

  // Arrow-key navigation — suppressed while a form control has focus so the
  // Details edit inputs keep their caret behavior.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) {
        return;
      }
      if (e.key === 'ArrowRight') advance(1);
      if (e.key === 'ArrowLeft') advance(-1);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [advance]);

  return { act, opt, detailsMode, setDetailsMode, go, setOpt, advance, goToDetailsEdit };
}
